import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, Polyline, CircleMarker, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import {
  Navigation, Thermometer, Package, Battery, BatteryLow,
  AlertTriangle, MapPin, X, Wifi, ChevronRight, Route, Eye, EyeOff, Clock
} from 'lucide-react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

delete L.Icon.Default.prototype._getIconUrl;

// Recenter map when device locations load
const RecenterMap = ({ locations }) => {
  const map = useMap();
  const hasCentered = useRef(false);
  useEffect(() => {
    if (locations.length > 0 && !hasCentered.current) {
      hasCentered.current = true;
      if (locations.length === 1) {
        map.setView([locations[0].lat, locations[0].lon], 13, { animate: true });
      } else {
        const bounds = L.latLngBounds(locations.map(l => [l.lat, l.lon]));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14, animate: true });
      }
    }
  }, [locations, map]);
  return null;
};

const FitToTrail = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points && points.length >= 2) {
      const bounds = L.latLngBounds(points.map(p => [p.lat, p.lon]));
      map.fitBounds(bounds, { padding: [80, 80], maxZoom: 15, animate: true });
    }
  }, [points, map]);
  return null;
};


// Device health assessment — only flag issues that actually matter
const getDeviceHealth = (device) => {
  const issues = [];
  if (device.battery_level != null && device.battery_level < 20) issues.push({ type: 'critical', label: 'Battery critical', icon: BatteryLow });
  else if (device.battery_level != null && device.battery_level < 35) issues.push({ type: 'warning', label: 'Battery low', icon: BatteryLow });
  // Only flag temperature if there's no recent data (stale device)
  const lastSeen = device.last_seen ? new Date(device.last_seen) : null;
  const hoursSinceReport = lastSeen ? (Date.now() - lastSeen.getTime()) / 3600000 : null;
  if (!device.last_seen) issues.push({ type: 'info', label: 'Never reported', icon: Wifi });
  else if (hoursSinceReport > 24) issues.push({ type: 'warning', label: 'Offline >24h', icon: Wifi });
  return issues;
};

const getMarkerColor = (device) => {
  const health = getDeviceHealth(device);
  if (health.some(h => h.type === 'critical')) return '#ef4444';
  if (health.some(h => h.type === 'warning')) return '#f59e0b';
  if (device.last_seen) return '#10b981';
  return '#3b82f6';
};

const makeIcon = (color, size = 28) => L.divIcon({
  className: '',
  html: `<div style="
    background:${color};
    width:${size}px;height:${size}px;
    border-radius:50%;
    border:3px solid white;
    box-shadow:0 2px 8px rgba(0,0,0,0.35);
    transition: transform 0.15s;
  "></div>`,
  iconSize: [size, size],
  iconAnchor: [size / 2, size / 2],
  popupAnchor: [0, -size / 2],
  tooltipAnchor: [size / 2, 0],
});

const clusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const children = cluster.getAllChildMarkers();
  // Check if any child has issues
  const hasIssue = children.some(m => {
    const el = m.getElement();
    return el?.querySelector('div')?.style?.background === 'rgb(239, 68, 68)' ||
           el?.querySelector('div')?.style?.background === 'rgb(245, 158, 11)';
  });
  const bg = hasIssue
    ? 'linear-gradient(135deg, #f59e0b, #ef4444)'
    : 'linear-gradient(135deg, #2563eb, #06b6d4)';
  return L.divIcon({
    html: `<div style="
      background:${bg};color:white;border-radius:50%;
      width:48px;height:48px;display:flex;align-items:center;justify-content:center;
      font-weight:700;font-size:16px;border:3px solid white;
      box-shadow:0 3px 10px rgba(0,0,0,0.3);cursor:pointer;
    ">${count}</div>`,
    className: '',
    iconSize: L.point(48, 48),
    iconAnchor: L.point(24, 24),
  });
};

const Map = () => {
  const { user } = useAuth();
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showRoutes, setShowRoutes] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyHours, setHistoryHours] = useState(48);
  const [selectedRouteShipment, setSelectedRouteShipment] = useState(null);
  const [selectedTrailDevice, setSelectedTrailDevice] = useState(null);

  const { data: devices = [] } = useQuery({
    queryKey: ['devices'],
    queryFn: async () => (await axios.get(`${API}/devices`)).data,
    refetchInterval: 10000,
  });

  const { data: shipments = [] } = useQuery({
    queryKey: ['shipments'],
    queryFn: async () => (await axios.get(`${API}/shipments`)).data,
    refetchInterval: 10000,
  });

  const { data: telemetryData = [] } = useQuery({
    queryKey: ['telemetry-map'],
    queryFn: async () => {
      try { return (await axios.get(`${API}/telemetry?limit=100`)).data; }
      catch { return []; }
    },
    refetchInterval: 10000,
  });

  // Route data for active shipments
  const { data: activeRoutes = [] } = useQuery({
    queryKey: ['active-routes'],
    queryFn: async () => {
      try { return (await axios.get(`${API}/routes/active`)).data; }
      catch { return []; }
    },
    refetchInterval: 30000,
    enabled: showRoutes,
  });

  // Device movement trails (history — independent of shipments)
  const { data: deviceTrails = [] } = useQuery({
    queryKey: ['device-trails', historyHours],
    queryFn: async () => {
      try { return (await axios.get(`${API}/devices/history/trails?hours=${historyHours}`)).data; }
      catch { return []; }
    },
    refetchInterval: 60000,
    enabled: showHistory,
  });

  // Single device detailed history
  const { data: deviceHistory } = useQuery({
    queryKey: ['device-history', selectedTrailDevice, historyHours],
    queryFn: async () => {
      if (!selectedTrailDevice) return null;
      try { return (await axios.get(`${API}/devices/${selectedTrailDevice}/history?hours=${historyHours}`)).data; }
      catch { return null; }
    },
    enabled: !!selectedTrailDevice,
  });

  // Detailed route for selected shipment
  const { data: selectedRoute } = useQuery({
    queryKey: ['shipment-route', selectedRouteShipment],
    queryFn: async () => {
      if (!selectedRouteShipment) return null;
      try { return (await axios.get(`${API}/shipments/${selectedRouteShipment}/route`)).data; }
      catch { return null; }
    },
    enabled: !!selectedRouteShipment,
  });

  const getDeviceLocation = useCallback((device) => {
    const shipment = shipments.find(s => s.device_id === device.device_id);
    if (shipment?.current_latitude && shipment?.current_longitude)
      return { lat: shipment.current_latitude, lon: shipment.current_longitude };
    if (device.last_latitude && device.last_longitude)
      return { lat: device.last_latitude, lon: device.last_longitude };
    return null;
  }, [shipments]);

  const locations = devices.map(d => getDeviceLocation(d)).filter(Boolean);

  // Compute health stats
  const devicesWithHealth = devices.map(d => {
    const t = telemetryData.find(tl => tl.device_id === d.device_id);
    return { ...d, _telemetry: t, _health: getDeviceHealth(d), _location: getDeviceLocation(d) };
  });
  const problemDevices = devicesWithHealth.filter(d => d._health.length > 0);
  const selectedData = selectedDevice ? devicesWithHealth.find(d => d.device_id === selectedDevice) : null;
  const selectedShipment = selectedDevice ? shipments.find(s => s.device_id === selectedDevice) : null;

  return (
    <div className="space-y-4" data-testid="map-page">
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-slate-900">Fleet Map</h2>
            <p className="text-slate-600 mt-1">Real-time GPS tracking — click markers for details</p>
          </div>
          {/* Route & History Toggles — visible to superadmin and owner */}
          {['superadmin', 'owner', 'support'].includes(user?.role) && (
            <div className="flex items-center gap-2">
              <Button
                variant={showRoutes ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowRoutes(!showRoutes)}
                data-testid="toggle-routes-btn"
                className={showRoutes ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                <Route className="w-4 h-4 mr-1.5" />
                Routes {showRoutes ? 'ON' : 'OFF'}
              </Button>
              <Button
                variant={showHistory ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setShowHistory(!showHistory); if (!showHistory) setSelectedTrailDevice(null); }}
                data-testid="toggle-history-btn"
                className={showHistory ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
              >
                <Clock className="w-4 h-4 mr-1.5" />
                History {showHistory ? 'ON' : 'OFF'}
              </Button>
              {showHistory && (
                <select
                  className="text-xs border rounded-md px-2 py-1.5 bg-white text-slate-700"
                  value={historyHours}
                  onChange={e => { setHistoryHours(Number(e.target.value)); setSelectedTrailDevice(null); }}
                  data-testid="history-hours-select"
                >
                  <option value={6}>6h</option>
                  <option value={24}>24h</option>
                  <option value={48}>48h</option>
                  <option value={168}>7 days</option>
                  <option value={720}>30 days</option>
                </select>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Devices</p>
              <p className="text-xl font-bold">{devices.length}</p>
            </div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Navigation className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">In Transit</p>
              <p className="text-xl font-bold">{shipments.filter(s => ['activated', 'in_transit'].includes(s.status)).length}</p>
            </div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Needs Attention</p>
              <p className="text-xl font-bold">{problemDevices.length}</p>
            </div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Thermometer className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Avg Temp</p>
              <p className="text-xl font-bold">
                {telemetryData.length > 0
                  ? (telemetryData.reduce((s, t) => s + (t.temperature || 0), 0) / telemetryData.length).toFixed(1)
                  : '--'}°C
              </p>
            </div>
          </div>
        </CardContent></Card>
      </div>

      {/* Map + Detail Panel */}
      <div className="flex gap-4">
        {/* Map */}
        <div className={`flex-1 rounded-2xl overflow-hidden border border-slate-200 shadow-lg transition-all ${selectedDevice ? '' : ''}`}>
          <div style={{ height: '600px', width: '100%' }}>
            <MapContainer center={[52.22, 5.17]} zoom={9} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <RecenterMap locations={locations} />
              <MarkerClusterGroup
                chunkedLoading
                maxClusterRadius={60}
                spiderfyOnMaxZoom={true}
                zoomToBoundsOnClick={true}
                spiderfyDistanceMultiplier={2.5}
                showCoverageOnHover={false}
                disableClusteringAtZoom={18}
                iconCreateFunction={clusterIcon}
              >
                {devicesWithHealth.map((device) => {
                  if (!device._location) return null;
                  const color = getMarkerColor(device);
                  const t = device._telemetry;
                  const shipment = shipments.find(s => s.device_id === device.device_id);

                  return (
                    <Marker
                      key={device.device_id}
                      position={[device._location.lat, device._location.lon]}
                      icon={makeIcon(color)}
                      eventHandlers={{
                        click: () => setSelectedDevice(device.device_id),
                      }}
                    >
                      <Tooltip direction="top" offset={[0, -16]} opacity={0.95}>
                        <div className="text-xs min-w-[140px]">
                          <div className="font-bold text-sm">{device.name}</div>
                          <div className="flex justify-between gap-4 mt-1">
                            <span>Temp:</span>
                            <span className="font-bold">{t?.temperature != null ? `${t.temperature.toFixed(1)}°C` : '--'}</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span>Battery:</span>
                            <span className="font-bold">{device.battery_level != null ? `${Math.round(device.battery_level)}%` : '--'}</span>
                          </div>
                          {shipment && (
                            <div className="mt-1 pt-1 border-t text-[10px] text-slate-500 truncate">
                              {shipment.origin?.split(',')[0]} → {shipment.destination?.split(',')[0]}
                            </div>
                          )}
                          {device.assigned_receiver_name && (
                            <div className="text-[10px] text-purple-600 font-medium truncate">
                              Receiver: {device.assigned_receiver_name}
                            </div>
                          )}
                          {device._health.length > 0 && (
                            <div className="mt-1 pt-1 border-t text-[10px] text-red-600 font-semibold">
                              {device._health.map(h => h.label).join(', ')}
                            </div>
                          )}
                        </div>
                      </Tooltip>
                    </Marker>
                  );
                })}
              </MarkerClusterGroup>

              {/* Route Polylines */}
              {showRoutes && activeRoutes.map((route) => (
                <Polyline
                  key={route.shipment_id}
                  positions={route.points.map(p => [p.lat, p.lon])}
                  pathOptions={{
                    color: selectedRouteShipment === route.shipment_id ? '#2563eb' : '#6366f1',
                    weight: selectedRouteShipment === route.shipment_id ? 4 : 2,
                    opacity: selectedRouteShipment === route.shipment_id ? 0.9 : 0.5,
                    dashArray: selectedRouteShipment === route.shipment_id ? null : '8 4',
                  }}
                  eventHandlers={{
                    click: () => setSelectedRouteShipment(
                      selectedRouteShipment === route.shipment_id ? null : route.shipment_id
                    ),
                  }}
                >
                  <Tooltip sticky>
                    <div className="text-xs">
                      <div className="font-bold">{route.sscc}</div>
                      <div>{route.origin} → {route.destination}</div>
                      <div className="text-slate-500">{route.points.length} points</div>
                    </div>
                  </Tooltip>
                </Polyline>
              ))}

              {/* Detailed route for selected shipment — with alarm markers */}
              {selectedRoute && selectedRoute.route_points.length > 1 && (
                <>
                  <Polyline
                    positions={selectedRoute.route_points.map(p => [p.lat, p.lon])}
                    pathOptions={{ color: '#2563eb', weight: 4, opacity: 0.9 }}
                  />
                  {/* Start point */}
                  <CircleMarker
                    center={[selectedRoute.route_points[0].lat, selectedRoute.route_points[0].lon]}
                    radius={6}
                    pathOptions={{ color: '#059669', fillColor: '#10b981', fillOpacity: 1, weight: 2 }}
                  >
                    <Tooltip>Start: {selectedRoute.origin}</Tooltip>
                  </CircleMarker>
                  {/* End point */}
                  <CircleMarker
                    center={[selectedRoute.route_points[selectedRoute.route_points.length - 1].lat, selectedRoute.route_points[selectedRoute.route_points.length - 1].lon]}
                    radius={6}
                    pathOptions={{ color: '#dc2626', fillColor: '#ef4444', fillOpacity: 1, weight: 2 }}
                  >
                    <Tooltip>Latest: {selectedRoute.destination}</Tooltip>
                  </CircleMarker>
                  {/* Alarm points on route */}
                  {selectedRoute.alarms.map((alarm, i) => {
                    // Find nearest route point for this alarm
                    const alarmTime = alarm.triggered_at;
                    const nearestPoint = selectedRoute.route_points.reduce((best, p) =>
                      Math.abs(new Date(p.timestamp) - new Date(alarmTime)) < Math.abs(new Date(best.timestamp) - new Date(alarmTime)) ? p : best
                    , selectedRoute.route_points[0]);
                    return (
                      <CircleMarker
                        key={`alarm-${i}`}
                        center={[nearestPoint.lat, nearestPoint.lon]}
                        radius={5}
                        pathOptions={{
                          color: alarm.severity === 'CRITICAL' ? '#dc2626' : '#f59e0b',
                          fillColor: alarm.severity === 'CRITICAL' ? '#ef4444' : '#fbbf24',
                          fillOpacity: 0.8, weight: 2,
                        }}
                      >
                        <Tooltip>
                          <div className="text-xs">
                            <div className="font-bold text-red-600">{alarm.alarm_type}</div>
                            <div>Value: {alarm.current_value}</div>
                          </div>
                        </Tooltip>
                      </CircleMarker>
                    );
                  })}
                </>
              )}

              {/* Device History Trails */}
              {showHistory && deviceTrails.map((trail, idx) => {
                const isSelected = selectedTrailDevice === trail.device_id;
                const hasSelection = !!selectedTrailDevice;
                // Skip non-selected trails when one is selected
                if (hasSelection && !isSelected) return null;
                // Distinct color per device
                const palette = ['#e63946','#2a9d8f','#e9c46a','#264653','#f4a261','#6a4c93','#1982c4','#8ac926','#ff595e','#6d6875','#06d6a0'];
                const color = palette[idx % palette.length];
                return (
                  <Polyline
                    key={`trail-${trail.device_id}`}
                    positions={trail.points.map(p => [p.lat, p.lon])}
                    pathOptions={{
                      color: color,
                      weight: isSelected ? 5 : 3.5,
                      opacity: isSelected ? 1 : 0.7,
                      lineCap: 'round',
                      lineJoin: 'round',
                    }}
                    eventHandlers={{
                      click: () => setSelectedTrailDevice(
                        isSelected ? null : trail.device_id
                      ),
                    }}
                  >
                    <Tooltip sticky>
                      <div className="text-xs">
                        <div className="font-bold" style={{color}}>{trail.name}</div>
                        <div>{trail.point_count} positions ({historyHours}h)</div>
                        {isSelected
                          ? <div className="text-slate-500">Click to deselect</div>
                          : <div className="text-slate-500">Click to isolate</div>
                        }
                      </div>
                    </Tooltip>
                  </Polyline>
                );
              })}

              {/* Stationary devices (only 1 position — shown as pulsing circle) */}
              {showHistory && !selectedTrailDevice && (() => {
                // Get all device positions from the main data and find those NOT in trails
                const trailDeviceIds = new Set(deviceTrails.map(t => t.device_id));
                const stationaryDevices = (devices || []).filter(d =>
                  d.last_latitude && d.last_longitude && !trailDeviceIds.has(d.device_id)
                );
                return stationaryDevices.map((d, i) => (
                  <CircleMarker
                    key={`stationary-${d.device_id}`}
                    center={[d.last_latitude, d.last_longitude]}
                    radius={6}
                    pathOptions={{
                      color: '#9ca3af',
                      fillColor: '#d1d5db',
                      fillOpacity: 0.6,
                      weight: 2,
                      dashArray: '3 3',
                    }}
                  >
                    <Tooltip>
                      <div className="text-xs">
                        <div className="font-semibold text-slate-600">{d.name}</div>
                        <div className="text-slate-400">Stationary — no movement in {historyHours}h</div>
                      </div>
                    </Tooltip>
                  </CircleMarker>
                ));
              })()}

              {/* Auto-zoom to selected device trail */}
              {deviceHistory && deviceHistory.points.length > 1 && (
                <FitToTrail points={deviceHistory.points} />
              )}

              {/* Selected device detailed trail — start/end markers */}
              {deviceHistory && deviceHistory.points.length > 1 && (() => {
                // Find the trail's color
                const trailIdx = deviceTrails.findIndex(t => t.device_id === selectedTrailDevice);
                const palette = ['#e63946','#2a9d8f','#e9c46a','#264653','#f4a261','#6a4c93','#1982c4','#8ac926','#ff595e','#6d6875','#06d6a0'];
                const trailColor = palette[(trailIdx >= 0 ? trailIdx : 0) % palette.length];
                const first = deviceHistory.points[0];
                const last = deviceHistory.points[deviceHistory.points.length - 1];
                return (
                  <>
                    <CircleMarker
                      center={[first.lat, first.lon]}
                      radius={8}
                      pathOptions={{ color: '#fff', fillColor: trailColor, fillOpacity: 1, weight: 3 }}
                    >
                      <Tooltip>
                        <div className="text-xs">
                          <div className="font-bold">Start (48h ago)</div>
                          <div>{new Date(first.ts).toLocaleString()}</div>
                          {first.temp != null && <div>{first.temp.toFixed(1)}°C</div>}
                        </div>
                      </Tooltip>
                    </CircleMarker>
                    <CircleMarker
                      center={[last.lat, last.lon]}
                      radius={8}
                      pathOptions={{ color: '#fff', fillColor: '#dc2626', fillOpacity: 1, weight: 3 }}
                    >
                      <Tooltip>
                        <div className="text-xs">
                          <div className="font-bold">Latest position</div>
                          <div>{new Date(last.ts).toLocaleString()}</div>
                          {last.temp != null && <div>{last.temp.toFixed(1)}°C</div>}
                        </div>
                      </Tooltip>
                    </CircleMarker>
                  </>
                );
              })()}
            </MapContainer>
          </div>
        </div>

        {/* Detail Panel */}
        <div className={`transition-all duration-300 ${selectedDevice ? 'w-96' : 'w-0 overflow-hidden'}`}>
          {selectedData && (
            <Card className="h-[600px] overflow-y-auto" data-testid="device-detail-panel">
              <CardContent className="pt-4 space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-lg">{selectedData.name}</h3>
                    <p className="text-xs text-slate-500 font-mono">{selectedData.device_id}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setSelectedDevice(null)} data-testid="close-detail-panel">
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                {/* Health alerts */}
                {selectedData._health.length > 0 && (
                  <div className="space-y-2">
                    {selectedData._health.map((h) => (
                      <div key={h.label} className={`flex items-center gap-2 p-2.5 rounded-lg text-sm font-medium ${
                        h.type === 'critical' ? 'bg-red-50 text-red-700 border border-red-200' :
                        h.type === 'warning' ? 'bg-orange-50 text-orange-700 border border-orange-200' :
                        'bg-blue-50 text-blue-700 border border-blue-200'
                      }`}>
                        <h.icon className="w-4 h-4 flex-shrink-0" />
                        {h.label}
                      </div>
                    ))}
                  </div>
                )}

                {/* Telemetry */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-xl text-center">
                    <Thermometer className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                    <div className={`text-xl font-bold ${
                      selectedData._telemetry?.temperature > 12 ? 'text-red-600' :
                      selectedData._telemetry?.temperature > 8 ? 'text-orange-600' : 'text-green-600'
                    }`}>
                      {selectedData._telemetry?.temperature != null
                        ? `${selectedData._telemetry.temperature.toFixed(1)}°C`
                        : '--'}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Temperature</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl text-center">
                    <Battery className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                    <div className={`text-xl font-bold ${
                      selectedData.battery_level < 20 ? 'text-red-600' :
                      selectedData.battery_level < 35 ? 'text-orange-600' : 'text-green-600'
                    }`}>
                      {selectedData.battery_level != null ? `${Math.round(selectedData.battery_level)}%` : '--'}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      Battery {selectedData.battery_mv ? `(${selectedData.battery_mv}mV)` : ''}
                    </div>
                  </div>
                </div>

                {/* Location */}
                {selectedData._location && (
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-1">
                      <MapPin className="w-3.5 h-3.5" /> Location
                    </div>
                    <div className="text-sm font-mono">
                      {selectedData._location.lat.toFixed(5)}, {selectedData._location.lon.toFixed(5)}
                    </div>
                    {selectedData.last_seen && (
                      <div className="text-[10px] text-slate-500 mt-1">
                        Last seen: {new Date(selectedData.last_seen).toLocaleString()}
                      </div>
                    )}
                  </div>
                )}

                {/* GRAI */}
                {selectedData.grai && (
                  <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                    <div className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">GRAI</div>
                    <div className="font-mono text-xs mt-1 break-all">{selectedData.grai}</div>
                  </div>
                )}

                {/* Assigned Receiver */}
                {selectedData.assigned_receiver_name && (
                  <div className="p-3 bg-purple-50 rounded-xl border border-purple-100">
                    <div className="text-[10px] font-bold text-purple-700 uppercase tracking-wider">Assigned Receiver</div>
                    <div className="text-sm font-semibold mt-1">{selectedData.assigned_receiver_name}</div>
                  </div>
                )}

                {/* Active Shipment */}
                {selectedShipment && (
                  <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] font-bold text-green-700 uppercase tracking-wider">Active Shipment</div>
                      <Badge className="bg-green-100 text-green-700 text-[10px]">{selectedShipment.status}</Badge>
                    </div>
                    <div className="text-xs space-y-1.5">
                      <div>
                        <span className="text-slate-500">SSCC: </span>
                        <span className="font-mono font-semibold">{selectedShipment.sscc}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">From: </span>
                        <span className="font-semibold">{selectedShipment.origin}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">To: </span>
                        <span className="font-semibold">{selectedShipment.destination}</span>
                      </div>
                      {selectedShipment.expected_temp_min != null && (
                        <div>
                          <span className="text-slate-500">Temp range: </span>
                          <span className="font-semibold">{selectedShipment.expected_temp_min}°C to {selectedShipment.expected_temp_max}°C</span>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-3 text-xs"
                      onClick={() => {
                        setSelectedRouteShipment(selectedRouteShipment === selectedShipment.id ? null : selectedShipment.id);
                        if (!showRoutes) setShowRoutes(true);
                      }}
                      data-testid="show-route-btn"
                    >
                      <Route className="w-3 h-3 mr-1.5" />
                      {selectedRouteShipment === selectedShipment.id ? 'Hide Route' : 'Show Route'}
                    </Button>
                  </div>
                )}

                {/* Device History Button — always show for device */}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => {
                    const devId = selectedData.device_id;
                    setSelectedTrailDevice(selectedTrailDevice === devId ? null : devId);
                    if (!showHistory) setShowHistory(true);
                  }}
                  data-testid="show-device-history-btn"
                >
                  <Clock className="w-3 h-3 mr-1.5" />
                  {selectedTrailDevice === selectedData?.device_id ? 'Hide History' : 'Show 48h History'}
                </Button>

                {/* Device Info */}
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between py-1.5 border-b border-slate-100">
                    <span className="text-slate-500">Type</span>
                    <span className="font-semibold">{selectedData.type || '--'}</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-slate-100">
                    <span className="text-slate-500">IMEI</span>
                    <span className="font-mono">{selectedData.imei || selectedData.device_id}</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-slate-100">
                    <span className="text-slate-500">Firmware</span>
                    <span className="font-mono">{selectedData.firmware_version || '--'}</span>
                  </div>
                  <div className="flex justify-between py-1.5">
                    <span className="text-slate-500">Status</span>
                    <Badge className="bg-green-100 text-green-700 text-[10px]">{selectedData.status}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Problem devices list when no device selected */}
          {!selectedDevice && problemDevices.length > 0 && (
            <Card className="w-96" data-testid="problem-devices-panel">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Needs Attention ({problemDevices.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[540px] overflow-y-auto">
                {problemDevices.map(d => (
                  <button
                    key={d.device_id}
                    onClick={() => setSelectedDevice(d.device_id)}
                    className="w-full text-left p-3 bg-slate-50 hover:bg-orange-50 rounded-xl border border-slate-200 hover:border-orange-200 transition-all"
                    data-testid={`problem-device-${d.device_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm">{d.name}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {d._health.map((h) => (
                        <span key={h.label} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          h.type === 'critical' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                        }`}>
                          {h.label}
                        </span>
                      ))}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 font-mono">{d.device_id}</div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Legend */}
      <Card>
        <CardContent className="py-3 px-4">
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <span className="text-xs font-semibold text-slate-500 uppercase">Legend:</span>
            <div className="flex items-center gap-2"><div className="w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-white shadow"></div>Normal</div>
            <div className="flex items-center gap-2"><div className="w-3.5 h-3.5 rounded-full bg-yellow-500 border-2 border-white shadow"></div>Warning</div>
            <div className="flex items-center gap-2"><div className="w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-white shadow"></div>Critical</div>
            <div className="flex items-center gap-2"><div className="w-3.5 h-3.5 rounded-full bg-blue-500 border-2 border-white shadow"></div>No telemetry</div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 text-white text-xs flex items-center justify-center font-bold border-2 border-white shadow">5</div>
              Cluster (click to expand)
            </div>
            {showRoutes && (
              <>
                <div className="flex items-center gap-2"><div className="w-6 h-0.5 bg-indigo-500" style={{borderBottom:'2px dashed #6366f1'}}></div>Shipment Route</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-green-500"></div>Start</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-500"></div>Latest</div>
              </>
            )}
            {showHistory && (
              <>
                <div className="flex items-center gap-2"><div className="w-6 h-0.5 bg-purple-400" style={{borderBottom:'2px dashed #a78bfa'}}></div>Device Trail</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-purple-500"></div>First (48h)</div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Map;
